import { Navigate, Route, Routes, useLocation } from 'react-router-dom';
import { FullPageSpinner } from './components/ui';
import { isLeader, useSession } from './lib/session';
import { AppLayout } from './layouts/AppLayout';
import { SignInPage } from './pages/auth/SignIn';
import { ForgotPasswordPage } from './pages/auth/ForgotPassword';
import { SignUpPage } from './pages/auth/SignUp';
import { JoinPage } from './pages/Join';
import { WelcomePage } from './pages/Welcome';
import { CallbackPage } from './pages/auth/Callback';
import { OverviewPage } from './pages/Overview';
import { PeoplePage } from './pages/People';
import { AccountPage } from './pages/Account';
import { SchoolSettingsPage } from './pages/SchoolSettings';
import { DevelopmentPage } from './pages/Development';
import { CommunityPage } from './pages/Community';
import { AdminReflectionModelsPage } from './pages/admin/AdminReflectionModels';
import { FrameworksPage } from './pages/Frameworks';
import { SharedPage, SharedReportPage } from './pages/Shared';
import { HubPage } from './pages/Hub';
import { AdminSchoolsPage } from './pages/admin/AdminSchools';
import { AdminSchoolPage } from './pages/admin/AdminSchool';
import { AdminHealthPage } from './pages/admin/AdminHealth';
import { AdminFrameworksPage } from './pages/admin/AdminFrameworks';
import { AdminFrameworkEditPage } from './pages/admin/AdminFrameworkEdit';
import { AdminFrameworkRequestsPage } from './pages/admin/AdminFrameworkRequests';
import { AdminFrameworkOrgsPage } from './pages/admin/FrameworkOrgs';
import { NotFoundPage, PersonalGate, RoleGate, SetupPendingGate, PausedGate, RemovedGate, TeacherGate, UnregisteredGate } from './pages/Gates';

export function App() {
  const { state } = useSession();
  const location = useLocation();

  if (state.status === 'loading') return <FullPageSpinner />;

  // Invite links work whoever is (or isn't) signed in.
  if (location.pathname.startsWith('/join/')) {
    return (
      <Routes>
        <Route path="/join/:token" element={<JoinPage />} />
      </Routes>
    );
  }

  if (state.status === 'signedOut') {
    return (
      <Routes>
        <Route path="/sign-in" element={<SignInPage />} />
        <Route path="/forgot-password" element={<ForgotPasswordPage />} />
        <Route path="/sign-up" element={<SignUpPage />} />
        <Route path="/auth/callback" element={<CallbackPage />} />
        <Route path="*" element={<Navigate to="/sign-in" replace state={{ from: location.pathname }} />} />
      </Routes>
    );
  }

  if (state.status === 'unregistered') return <UnregisteredGate />;
  // No organisation: removed, declined, or waiting for approval after joining with a code.
  if (state.me.join_status && state.me.join_status !== 'active') return <RemovedGate />;
  if (Number(state.me.school_paused) && state.me.role !== 'system_admin') return <PausedGate />;
  // The owner of a personal account is a teacher who still sets up their own account here.
  const ownsPersonal = state.me.school_type === 'personal' && !!Number(state.me.is_owner);
  if (ownsPersonal && (!state.me.school_onboarded_at || state.me.school_setup_complete === false)) {
    return (
      <Routes>
        <Route path="/welcome" element={<WelcomePage />} />
        <Route path="*" element={<Navigate to="/welcome" replace />} />
      </Routes>
    );
  }
  if (ownsPersonal) return <PersonalGate />;
  if (!isLeader(state.me.role)) return <TeacherGate />;

  // Setup (including accepting the terms) comes first; the rest of the app
  // opens once it's done, and leaders of schools that finished it with gaps
  // are sent back to fill them. EducAitor admins aren't held up by it.
  const setupDone = !!state.me.school_onboarded_at && state.me.school_setup_complete !== false;
  // Owners finish setup; EducAitor admins can too, for organisations they're in.
  const canFinishSetup = !!Number(state.me.is_owner) || state.me.role === 'system_admin';
  if (!setupDone && state.me.role !== 'system_admin') {
    // Only an owner sets the school up (they agree the terms for it).
    if (!Number(state.me.is_owner)) return <SetupPendingGate />;
    return (
      <Routes>
        <Route path="/welcome" element={<WelcomePage />} />
        <Route path="*" element={<Navigate to="/welcome" replace />} />
      </Routes>
    );
  }

  // A leader joining a school that's already set up only needs their own role.
  if (!state.me.job_title && state.me.role !== 'system_admin') return <RoleGate />;

  return (
    <Routes>
      <Route path="/sign-in" element={<Navigate to="/" replace />} />
      <Route path="/forgot-password" element={<Navigate to="/" replace />} />
      <Route path="/sign-up" element={<Navigate to="/" replace />} />
      <Route path="/auth/callback" element={<Navigate to="/" replace />} />
      {/* Setup is finished: answers are edited in Organisation settings now (O-05).
          An admin who owns an unfinished organisation can still open it. */}
      <Route path="/welcome" element={!setupDone && canFinishSetup ? <WelcomePage /> : <Navigate to="/" replace />} />
      <Route element={<AppLayout />}>
        <Route index element={<OverviewPage />} />
        <Route path="people" element={<PeoplePage />} />
        <Route path="hub" element={<HubPage />} />
        <Route path="development" element={<DevelopmentPage />} />
        <Route path="community" element={<CommunityPage />} />
        <Route path="shared" element={<SharedPage />} />
        <Route path="shared/:id" element={<SharedReportPage />} />
        <Route path="frameworks" element={<FrameworksPage />} />
        <Route path="school" element={<SchoolSettingsPage />} />
        <Route path="account" element={<AccountPage />} />
        {state.me.role === 'system_admin' ? (
          <>
            <Route path="admin/schools" element={<AdminSchoolsPage />} />
            <Route path="admin/schools/:id" element={<AdminSchoolPage />} />
            {/* People are managed inside their organisation now; one search finds both. */}
            <Route path="admin/users" element={<Navigate to="/admin/schools" replace />} />
            <Route path="admin/reviews" element={<Navigate to="/admin/framework-requests" replace />} />
            <Route path="admin/frameworks" element={<AdminFrameworksPage />} />
            <Route path="admin/frameworks/:id" element={<AdminFrameworkEditPage />} />
            <Route path="admin/frameworks/:id/organisations" element={<AdminFrameworkOrgsPage />} />
            <Route path="admin/framework-requests" element={<AdminFrameworkRequestsPage />} />
            <Route path="admin/framework-requests/:id" element={<AdminFrameworkRequestsPage />} />
            <Route path="admin/health" element={<AdminHealthPage />} />
            <Route path="admin/reflection-models" element={<AdminReflectionModelsPage />} />
          </>
        ) : null}
        <Route path="*" element={<NotFoundPage />} />
      </Route>
    </Routes>
  );
}
